import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const InvoiceTable = ({ invoices, onViewInvoice, onEditInvoice, onDeleteInvoice, onUpdateStatus, onPreviewInvoice }) => {
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'invoiceDate', direction: 'desc' });

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedInvoices(invoices.map(invoice => invoice.id));
    } else {
      setSelectedInvoices([]);
    }
  };

  const handleSelectInvoice = (invoiceId, checked) => {
    if (checked) {
      setSelectedInvoices(prev => [...prev, invoiceId]);
    } else {
      setSelectedInvoices(prev => prev.filter(id => id !== invoiceId));
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedInvoices = [...invoices].sort((a, b) => {
    if (sortConfig.key) {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (sortConfig.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    }
    return 0;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      'paid': { bg: 'bg-success/10', text: 'text-success', icon: 'CheckCircle' },
      'pending': { bg: 'bg-warning/10', text: 'text-warning', icon: 'Clock' },
      'overdue': { bg: 'bg-error/10', text: 'text-error', icon: 'AlertCircle' },
      'draft': { bg: 'bg-muted', text: 'text-muted-foreground', icon: 'FileText' }
    };

    const config = statusConfig[status] || statusConfig.draft;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon name={config.icon} size={12} />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <Icon name="ArrowUpDown" size={14} className="text-muted-foreground" />;
    }
    return sortConfig.direction === 'asc' 
      ? <Icon name="ArrowUp" size={14} className="text-primary" />
      : <Icon name="ArrowDown" size={14} className="text-primary" />;
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Table Header */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={selectedInvoices.length === invoices.length && invoices.length > 0}
                onChange={(e) => handleSelectAll(e.target.checked)}
                className="rounded border-border"
              />
              <span className="text-sm text-muted-foreground">
                {selectedInvoices.length > 0 ? `${selectedInvoices.length} selected` : 'Select all'}
              </span>
            </label>
          </div>
          
          {selectedInvoices.length > 0 && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm">
                <Icon name="Send" size={14} className="mr-1" />
                Send Reminders
              </Button>
              <Button variant="outline" size="sm">
                <Icon name="Download" size={14} className="mr-1" />
                Export
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/20">
            <tr>
              <th className="text-left p-4 font-medium text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  Select
                </div>
              </th>
              <th 
                className="text-left p-4 font-medium text-sm text-muted-foreground cursor-pointer hover:text-foreground"
                onClick={() => handleSort('invoiceNumber')}
              >
                <div className="flex items-center gap-2">
                  Invoice #
                  {getSortIcon('invoiceNumber')}
                </div>
              </th>
              <th 
                className="text-left p-4 font-medium text-sm text-muted-foreground cursor-pointer hover:text-foreground"
                onClick={() => handleSort('clientName')}
              >
                <div className="flex items-center gap-2">
                  Client
                  {getSortIcon('clientName')}
                </div>
              </th>
              <th 
                className="text-left p-4 font-medium text-sm text-muted-foreground cursor-pointer hover:text-foreground"
                onClick={() => handleSort('totalAmount')}
              >
                <div className="flex items-center gap-2">
                  Amount
                  {getSortIcon('totalAmount')}
                </div>
              </th>
              <th 
                className="text-left p-4 font-medium text-sm text-muted-foreground cursor-pointer hover:text-foreground"
                onClick={() => handleSort('invoiceDate')}
              >
                <div className="flex items-center gap-2">
                  Date
                  {getSortIcon('invoiceDate')}
                </div>
              </th>
              <th 
                className="text-left p-4 font-medium text-sm text-muted-foreground cursor-pointer hover:text-foreground"
                onClick={() => handleSort('dueDate')}
              >
                <div className="flex items-center gap-2">
                  Due Date
                  {getSortIcon('dueDate')}
                </div>
              </th>
              <th className="text-left p-4 font-medium text-sm text-muted-foreground">Status</th>
              <th className="text-right p-4 font-medium text-sm text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedInvoices.map((invoice) => (
              <tr key={invoice.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                <td className="p-4">
                  <input
                    type="checkbox"
                    checked={selectedInvoices.includes(invoice.id)}
                    onChange={(e) => handleSelectInvoice(invoice.id, e.target.checked)}
                    className="rounded border-border"
                  />
                </td>
                <td className="p-4">
                  <div className="font-medium text-foreground">{invoice.invoiceNumber}</div>
                  <div className="text-xs text-muted-foreground">{invoice.sacCode}</div>
                </td>
                <td className="p-4">
                  <div className="font-medium text-foreground">{invoice.clientName}</div>
                  <div className="text-xs text-muted-foreground">{invoice.clientEmail}</div>
                </td>
                <td className="p-4">
                  <div className="font-medium text-foreground">{formatCurrency(invoice.totalAmount)}</div>
                  <div className="text-xs text-muted-foreground">
                    GST: {formatCurrency(invoice.gstAmount)}
                  </div>
                </td>
                <td className="p-4 text-sm text-muted-foreground">
                  {formatDate(invoice.invoiceDate)}
                </td>
                <td className="p-4 text-sm text-muted-foreground">
                  {formatDate(invoice.dueDate)}
                </td>
                <td className="p-4">
                  {getStatusBadge(invoice.status)}
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onViewInvoice(invoice)}
                      className="h-8 w-8"
                      title="View Details"
                    >
                      <Icon name="Eye" size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onPreviewInvoice && onPreviewInvoice(invoice)}
                      className="h-8 w-8"
                      title="Preview & Print"
                    >
                      <Icon name="FileText" size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEditInvoice(invoice)}
                      className="h-8 w-8"
                      title="Edit"
                    >
                      <Icon name="Edit" size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDeleteInvoice(invoice.id)}
                      className="h-8 w-8 text-error hover:text-error"
                      title="Delete"
                    >
                      <Icon name="Trash2" size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden">
        {sortedInvoices.map((invoice) => (
          <div key={invoice.id} className="p-4 border-b border-border">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={selectedInvoices.includes(invoice.id)}
                  onChange={(e) => handleSelectInvoice(invoice.id, e.target.checked)}
                  className="rounded border-border"
                />
                <div>
                  <div className="font-medium text-foreground">{invoice.invoiceNumber}</div>
                  <div className="text-sm text-muted-foreground">{invoice.clientName}</div>
                </div>
              </div>
              {getStatusBadge(invoice.status)}
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <div className="text-xs text-muted-foreground">Amount</div>
                <div className="font-medium text-foreground">{formatCurrency(invoice.totalAmount)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Due Date</div>
                <div className="text-sm text-foreground">{formatDate(invoice.dueDate)}</div>
              </div>
            </div>
            
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onViewInvoice(invoice)}
              >
                <Icon name="Eye" size={14} className="mr-1" />
                View
              </Button>
              {onPreviewInvoice && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPreviewInvoice(invoice)}
                >
                  <Icon name="FileText" size={14} className="mr-1" />
                  Preview
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEditInvoice(invoice)}
              >
                <Icon name="Edit" size={14} className="mr-1" />
                Edit
              </Button>
            </div>
          </div>
        ))}
      </div>

      {invoices.length === 0 && (
        <div className="p-12 text-center">
          <Icon name="FileText" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No invoices found</h3>
          <p className="text-muted-foreground">Create your first invoice to get started</p>
        </div>
      )}
    </div>
  );
};

export default InvoiceTable;